import { useRef } from "react";
import { Alert, Box, Button, Chip, Divider, Stack, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import type { CoachingProposal } from "@bw/shared/api";
import { useAcceptCoachingProposal, useDecideCoachingProposal } from "@bw/shared/hooks";
import { coachingCancellationSummary, formatMoney } from "@bw/shared/utils";

const labels = {
  accept: "Accept proposal",
  decline: "Decline",
  request_revision: "Request changes",
  withdraw: "Withdraw proposal",
} as const;

function time(value: string, timezone: string) {
  return new Date(value).toLocaleString("en-US", {
    timeZone: timezone, dateStyle: "medium", timeStyle: "short",
  });
}

export function CoachingProposalTimelineCard({ proposal, canAct }: {
  proposal: CoachingProposal;
  canAct: () => boolean;
}) {
  const accept = useAcceptCoachingProposal();
  const decide = useDecideCoachingProposal();
  const keys = useRef(new Map<string, string>());
  const busy = useRef(false);
  const navigate = useNavigate();
  const terms = proposal.terms;
  const schedule = terms.schedule;
  const reschedule = proposal.replaces_acceptance_id !== null;

  async function act(action: keyof typeof labels) {
    if (!canAct() || busy.current || !proposal.available_actions.includes(action)) return;
    busy.current = true;
    const idempotency_key = keys.current.get(action) ?? crypto.randomUUID();
    keys.current.set(action, idempotency_key);
    const body = { revision_id: proposal.revision_id, expected_revision: proposal.revision, idempotency_key };
    try {
      if (action === "accept") {
        const accepted = await accept.mutateAsync([proposal.request_id, body]);
        const tab = proposal.viewer_roles.includes("payer") ? "payments" : "lessons";
        navigate(`/me/lessons?tab=${tab}&acceptance_id=${encodeURIComponent(accepted.acceptance_id)}`);
      }
      else await decide.mutateAsync([proposal.request_id, { ...body, action, note: null }]);
    } catch {
      // Mutation state renders a retryable error; the exact key is retained.
    } finally {
      busy.current = false;
    }
  }

  return <Box component="section" aria-label="Coaching proposal" sx={{ maxWidth: 760, p: { xs: 1.5, sm: 2 },
    border: 1, borderColor: "divider", borderRadius: 2, bgcolor: "background.paper" }}>
    <Stack spacing={1.25}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ justifyContent: "space-between" }}>
        <Typography sx={{ fontWeight: 700 }}>{terms.service_name}</Typography>
        <Typography sx={{ fontWeight: 700 }}>{formatMoney(terms.total_amount, terms.currency)}</Typography>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
        <Chip size="small" variant="outlined" label={proposal.state.replaceAll("_", " ")} />
        <Chip size="small" variant="outlined" label="One payment" />
        <Chip size="small" variant="outlined" label={`${schedule.kind === "fixed" ? schedule.sessions.length : schedule.credits.length} ${
          (schedule.kind === "fixed" ? schedule.sessions.length : schedule.credits.length) === 1 ? "session" : "sessions"}`} />
      </Stack>
      <Typography variant="body2" color="text.secondary">Times in {schedule.timezone}</Typography>
      {schedule.kind === "fixed" ? schedule.sessions.map((session) => <Typography key={session.session_key} variant="body2">
        {time(session.starts_at, schedule.timezone)}–{time(session.ends_at, schedule.timezone)} · {session.court_ids.length
          ? session.court_ids.map((id) => proposal.courts.find((court) => court.court_id === id)?.name ?? "Court unavailable").join(", ")
          : "No court required"}
      </Typography>) : <>
        <Typography variant="body2">{schedule.credits.length} sessions · {schedule.session_duration_minutes} minutes each · {schedule.required_court_count} courts per session</Typography>
        <Typography variant="body2">Book from {time(schedule.booking_opens_at, schedule.timezone)} until {time(schedule.booking_closes_at, schedule.timezone)}.</Typography>
        <Typography variant="body2">Complete sessions by {time(schedule.sessions_complete_by, schedule.timezone)}.</Typography>
        <Typography variant="body2">Eligible courts: {schedule.allowed_court_ids.length
          ? schedule.allowed_court_ids.map((id) => proposal.courts.find((court) => court.court_id === id)?.name ?? "Court unavailable").join(", ")
          : "No court required"}</Typography>
      </>}
      <Typography variant="body2">Accept by {time(terms.expires_at, schedule.timezone)}. Payment due {time(terms.payment_due_at, schedule.timezone)}.</Typography>
      <Divider />
      <Typography variant="subtitle2">Cancellation terms</Typography>
      {coachingCancellationSummary(terms.cancellation).map((line) => <Typography key={line} variant="body2">{line}</Typography>)}
      <Typography variant="body2">No-show refund: {terms.cancellation.no_show_refund_basis_points / 100}%. {terms.cancellation.no_show_consumes_credit
        ? "A no-show uses the session credit." : "A no-show does not use the session credit."}</Typography>
      {schedule.kind === "flexible_package" ? <Typography variant="body2">Unused credit refund: {terms.cancellation.unused_package_refund_basis_points / 100}%.</Typography> : null}
      {reschedule ? <Alert severity="info">{schedule.kind === "fixed"
        ? "Reschedule proposal: session dates, times, and single-court assignments may change. Price, participants, durations, and session count stay the same. No additional payment is required."
        : "Reschedule proposal: remaining booked work returns to available credits for rebooking within the accepted package window. Price, participants, duration, and credit count stay the same. No additional payment is required."}</Alert> : null}
      {accept.isError || decide.isError ? <Alert severity="error">The proposal changed or could not be updated. Refresh the conversation and try again.</Alert> : null}
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        {reschedule && proposal.available_actions.includes("accept") ? <Button component={RouterLink} size="small"
          variant="contained" to={`/me/lessons/${encodeURIComponent(proposal.request_id)}?revision_id=${encodeURIComponent(proposal.revision_id)}`}>
          Review reschedule
        </Button> : null}
        {proposal.available_actions.map((action) => action === "accept" && reschedule ? null : <Button key={action} size="small"
          variant={action === "accept" ? "contained" : "outlined"}
          disabled={!canAct() || accept.isPending || decide.isPending}
          onClick={() => void act(action)}>{labels[action]}</Button>)}
      </Stack>
      {proposal.acceptance_id ? <Button component={RouterLink} size="small" sx={{ alignSelf: "flex-start" }}
        to={proposal.viewer_roles.includes("payer")
          ? `/me/lessons?tab=payments&acceptance_id=${encodeURIComponent(proposal.acceptance_id)}`
          : `/me/lessons?tab=lessons&acceptance_id=${encodeURIComponent(proposal.acceptance_id)}`}>
        {proposal.viewer_roles.includes("payer") ? "Continue to payment and lessons" : "View lesson"}
      </Button> : null}
    </Stack>
  </Box>;
}
